import React, { createContext, useContext, useState, useCallback, useRef, useMemo } from "react";
import { useNodesState, useEdgesState } from "@xyflow/react";
import { useBluelagoon } from "@bluelagoon";
import { useWorkflow } from "../../../context/WorkflowContext";
import { useWorkflowExecution } from "../../../context/WorkflowExecutionContext";
import { buildNodesAndEdges } from "./stepToNode";
import useAutoLayout from "./hooks/useAutoLayout";

const CanvasContext = createContext(null);
const NOTE_STORAGE_KEY = "canvas-notes-";

function loadNotes(workflowId) {
    try {
        const raw = localStorage.getItem(NOTE_STORAGE_KEY + workflowId);
        return raw ? JSON.parse(raw) : [];
    } catch { return []; }
}

function saveNotes(workflowId, notes) {
    localStorage.setItem(NOTE_STORAGE_KEY + workflowId, JSON.stringify(notes));
}

function makeNoteNodes(notes) {
    return notes.map((n) => ({ id: n.id, type: "stickyNote", position: n.position, data: { text: n.text, colorIndex: n.colorIndex || 0 } }));
}

export function useCanvasContext() {
    return useContext(CanvasContext);
}

export function WorkflowCanvasProvider({ children }) {
    const { workflow, steps } = useWorkflow();
    const { call: saveStepDirect } = useBluelagoon({ method: "updateWorkflowStep" });
    const { stepExecutionResult, stepIdRunning, completedStepIds, failedStepId } = useWorkflowExecution();
    const { getLayoutedElements } = useAutoLayout();
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [executionState, setExecutionState] = useState(null);
    const lastSyncKeyRef = useRef("");
    const notesRef = useRef(loadNotes(workflow?.id));
    const lastExecRef = useRef({ result: null, running: null, failed: null, completed: null });

    const triggers = workflow?.workflowTriggers || [];

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, rawOnEdgesChange] = useEdgesState([]);
    const onEdgesChange = useCallback((changes) => {
        // Prevent React Flow from removing our programmatic edges
        const filtered = changes.filter((c) => c.type !== "remove");
        if (filtered.length > 0) rawOnEdgesChange(filtered);
    }, [rawOnEdgesChange]);

    const stepsMapRef = useRef(new Map());
    React.useEffect(() => {
        const m = new Map();
        steps.forEach((s, idx) => m.set(s.id, { step: s, idx }));
        stepsMapRef.current = m;
    }, [steps]);

    const applyLayout = useCallback((currentTriggers, currentSteps) => {
        const { nodes: rawNodes, edges: rawEdges } = buildNodesAndEdges(currentTriggers, currentSteps);
        const noteNodes = makeNoteNodes(notesRef.current);
        const allHavePositions = currentSteps.length > 0 && currentSteps.every((s) => s.config?.canvasPosition);

        if (allHavePositions) {
            // Position triggers relative to the first step rather than using dagre
            const sorted = [...currentSteps].sort((a, b) => a.order - b.order);
            const firstPos = sorted[0]?.config?.canvasPosition || { x: 300, y: 50 };
            const triggerCount = currentTriggers.length;
            const merged = rawNodes.map((n) => {
                if (n.id.startsWith("trigger-")) {
                    const trigIdx = rawNodes.filter((nd) => nd.id.startsWith("trigger-")).indexOf(n);
                    const totalHeight = triggerCount * 90;
                    const startY = firstPos.y - totalHeight / 2 + 45;
                    return { ...n, position: { x: firstPos.x - 370, y: startY + trigIdx * 90 } };
                }
                return n;
            });
            setNodes([...merged, ...noteNodes]);
        } else {
            const layouted = getLayoutedElements(rawNodes, rawEdges);
            setNodes([...layouted.nodes, ...noteNodes]);
        }
        setEdges(rawEdges);
    }, [getLayoutedElements, setNodes, setEdges]);

    React.useEffect(() => {
        const triggerIds = triggers.map((t) => t.id).join(",");
        const stepMeta = steps.map((s) => {
            const m = s.config?.nodeMetadata || {};
            return `${s.id}:${s.name}:${m.title || ""}:${m.subtitle || ""}:${m.notes || ""}:${m.icon || ""}`;
        }).join(",");
        const syncKey = `${triggerIds}|${stepMeta}`;
        if (syncKey === lastSyncKeyRef.current) return;
        lastSyncKeyRef.current = syncKey;
        applyLayout(triggers, steps);
    }, [triggers, steps, applyLayout]);

    const persistNotes = useCallback((updatedNodes) => {
        const notes = updatedNodes.filter((n) => n.type === "stickyNote").map((n) => ({
            id: n.id, position: n.position, text: n.data.text || "", colorIndex: n.data.colorIndex || 0,
        }));
        notesRef.current = notes;
        if (workflow?.id) saveNotes(workflow.id, notes);
    }, [workflow?.id]);

    const addNote = useCallback(() => {
        const id = `note-${Date.now()}`;
        const newNode = { id, type: "stickyNote", position: { x: 100, y: 100 }, data: { text: "", colorIndex: 0 } };
        setNodes((prev) => {
            const next = [...prev, newNode];
            persistNotes(next);
            return next;
        });
    }, [setNodes, persistNotes]);

    const updateNote = useCallback((noteId, updates) => {
        setNodes((prev) => {
            const next = prev.map((n) => n.id === noteId ? { ...n, data: { ...n.data, ...updates } } : n);
            persistNotes(next);
            return next;
        });
    }, [setNodes, persistNotes]);

    const deleteNote = useCallback((noteId) => {
        setNodes((prev) => {
            const next = prev.filter((n) => n.id !== noteId);
            persistNotes(next);
            return next;
        });
        setSelectedNodeId(null);
    }, [setNodes, persistNotes, setSelectedNodeId]);

    const saveNodePosition = useCallback((nodeId, position) => {
        if (nodeId.startsWith("note-")) {
            const notes = notesRef.current.map((n) =>
                n.id === nodeId ? { ...n, position: { x: Math.round(position.x), y: Math.round(position.y) } } : n
            );
            notesRef.current = notes;
            if (workflow?.id) saveNotes(workflow.id, notes);
            return;
        }
        if (nodeId.startsWith("trigger-")) return;
        const entry = stepsMapRef.current.get(nodeId);
        if (!entry) return;
        const config = { ...(entry.step.config || {}), canvasPosition: { x: Math.round(position.x), y: Math.round(position.y) } };
        saveStepDirect({ stepId: nodeId, config, isDraft: true });
    }, [saveStepDirect, workflow?.id]);

    const onNodeDragStop = useCallback((_event, node) => {
        saveNodePosition(node.id, node.position);
    }, [saveNodePosition]);

    // Sync execution results to nodes and edges — only update changed nodes
    React.useEffect(() => {
        if (!stepExecutionResult && !stepIdRunning && !failedStepId && completedStepIds.size === 0) return;

        const prev = lastExecRef.current;
        const resultChanged = prev.result !== stepExecutionResult;
        const runningChanged = prev.running !== stepIdRunning;
        const failedChanged = prev.failed !== failedStepId;
        const completedChanged = prev.completed !== completedStepIds;
        if (!resultChanged && !runningChanged && !failedChanged && !completedChanged) return;
        lastExecRef.current = { result: stepExecutionResult, running: stepIdRunning, failed: failedStepId, completed: completedStepIds };

        const statusMap = new Map();
        steps.forEach((step, idx) => {
            let status = "default";
            if (step.id === stepIdRunning) status = "running";
            else if (step.id === failedStepId) status = "error";
            else if (stepExecutionResult?.[idx] != null) {
                const r = stepExecutionResult[idx];
                status = r?.status === "error" ? "error" : r?.status === "skipped" ? "skipped" : "success";
            } else if (completedStepIds.has(step.id)) {
                status = "success";
            }
            statusMap.set(step.id, { status, result: stepExecutionResult?.[idx] ?? null });
        });

        setNodes((prev) => {
            let changed = false;
            const next = prev.map((n) => {
                if (n.type === "stickyNote" || n.id.startsWith("trigger-")) return n;
                const entry = statusMap.get(n.id);
                if (!entry || n.data.execution?.status === entry.status) return n;
                changed = true;
                return { ...n, data: { ...n.data, execution: entry } };
            });
            return changed ? next : prev;
        });

        setEdges((prev) => {
            let changed = false;
            const next = prev.map((e) => {
                const targetStatus = statusMap.get(e.target)?.status || "default";
                const edgeStatus = e.source.startsWith("trigger-")
                    ? (stepIdRunning ? "running" : targetStatus === "success" ? "success" : "default")
                    : targetStatus === "running" ? "running"
                    : (statusMap.get(e.source)?.status === "success" ? "success" : "default");
                if (e.data?.status === edgeStatus) return e;
                changed = true;
                return { ...e, data: { ...e.data, status: edgeStatus }, animated: edgeStatus === "running" };
            });
            return changed ? next : prev;
        });
    }, [stepExecutionResult, stepIdRunning, completedStepIds, failedStepId, steps, setNodes, setEdges]);

    const autoLayout = useCallback(() => {
        const stepNodes = nodes.filter((n) => n.type !== "stickyNote");
        const noteNodes = nodes.filter((n) => n.type === "stickyNote");
        const layouted = getLayoutedElements(stepNodes, edges);
        setNodes([...layouted.nodes, ...noteNodes]);
        setEdges(layouted.edges);
        layouted.nodes.forEach((n) => {
            if (n.id.startsWith("trigger-")) return;
            const entry = stepsMapRef.current.get(n.id);
            if (!entry) return;
            const config = { ...(entry.step.config || {}), canvasPosition: { x: Math.round(n.position.x), y: Math.round(n.position.y) } };
            saveStepDirect({ stepId: n.id, config, isDraft: true });
        });
    }, [nodes, edges, getLayoutedElements, setNodes, setEdges, saveStepDirect]);

    const clearStepNotes = useCallback((stepId) => {
        const entry = stepsMapRef.current.get(stepId);
        if (!entry) return;
        const config = { ...(entry.step.config || {}), nodeMetadata: { ...(entry.step.config?.nodeMetadata || {}), notes: "" } };
        saveStepDirect({ stepId, config, isDraft: true });
        setNodes((prev) => prev.map((n) => n.id === stepId ? { ...n, data: { ...n.data, notes: "" } } : n));
    }, [saveStepDirect, setNodes]);

    const value = useMemo(() => ({
        nodes, edges, onNodesChange, onEdgesChange, onNodeDragStop,
        setNodes, setEdges, selectedNodeId, setSelectedNodeId,
        executionState, setExecutionState, autoLayout,
        addNote, updateNote, deleteNote, clearStepNotes,
        workflowId: workflow?.id, workflowName: workflow?.name,
    }), [
        nodes, edges, onNodesChange, onEdgesChange, onNodeDragStop, setNodes, setEdges,
        selectedNodeId, executionState, autoLayout, addNote, updateNote, deleteNote, clearStepNotes,
        workflow?.id, workflow?.name,
    ]);

    return <CanvasContext.Provider value={value}>{children}</CanvasContext.Provider>;
}
